import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Button, Form, InputField } from '@/components';
import { PencilIcon } from '@/components/Navbar/Icons';
import { ShieldTeam } from '@/components/ShieldTeam';
import { TshirtTeam } from '@/components/TshirtTeam';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import useTshirtAndShield from '@/hooks/useTshirtAndShield';
import ROUTES from '@/router/routes';
import { getTshirtAndShield } from '@/services/editAccount';
import { changeTeamName } from '@/services/editProfile';
import { defaultShieldTeam, defaultTshirtTeam } from '@/store/tshirtAndShield';
import { myTeamSchema, TEXTS, validateFieldError } from '@/utils';

type FormValues = {
  teamName?: string;
};

type MyTeamProps = {
  onDirtyChange?: (isDirty: boolean) => void;
};

// TODO --------------------------------------------------------------
// adaptar a mobile el ShieldLayout

export const MyTeam = ({ onDirtyChange }: MyTeamProps) => {
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { userInitialData, setUserInitialData } = useAuth();
  const toast = useToast();
  const { isMobile } = useIsDesktop();
  const navigate = useNavigate();

  const { data: dataShieldTshirt, updateTshirtAndShield } =
    useTshirtAndShield();

  useQuery(
    'initialDataTshitAndShield',
    async () => await getTshirtAndShield(),
    {
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess: (data) => {
        if ('codError' in data && 'mensaje' in data) {
          toast.show(data.mensaje as string);
        } else {
          updateTshirtAndShield({
            tshirt: data?.svg.camiseta || dataShieldTshirt?.tshirt,
            shield: data?.svg.escudo || dataShieldTshirt?.shield,
          });
        }
      },
      onError: (error) => {
        const err = error as Error;
        toast.show(err.message);
      },
    }
  );

  const initialValues = useMemo(() => {
    return {
      teamName: userInitialData?.nombre_equipo,
    };
  }, [userInitialData]);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues,
    validateOnBlur: true,
    validationSchema: myTeamSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const response = await changeTeamName({
          nombreEquipo: values.teamName ?? '',
        });
        if (response) {
          if (response.estado) {
            toast.show(TEXTS.personalDataUpdated);
            if (userInitialData) {
              setUserInitialData({
                ...userInitialData,
                nombre_equipo: values.teamName ?? '',
              });
            }
            resetForm({ values });
            setIsFormDirty(false);
          } else {
            toast.show(response.mensaje);
          }
        }
      } catch (error) {}
      setSubmitting(false);
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const PencilButton = ({ onClick }: { onClick?: () => void }) => {
    return (
      <div
        className="h-10 w-10 mb-2 bg-gdt-blue rounded-full flex items-center justify-center cursor-pointer relative right-10"
        onClick={onClick || undefined}
      >
        <PencilIcon className="w-6 h-6" />
      </div>
    );
  };

  const {
    values,
    handleSubmit,
    isSubmitting,
    handleBlur,
    handleChange,
    errors,
    touched,
    dirty,
  } = formik;

  useEffect(() => {
    const isDirty = values.teamName !== initialValues.teamName;
    setIsFormDirty(isDirty);
    onDirtyChange?.(dirty);
  }, [values.teamName, initialValues.teamName, dirty, onDirtyChange]);

  return (
    <>
      <div className="lg:ml-[115px] p-4 lg:p-0 lg:mt-10">
        <Form.Form
          className="space-y-12"
          loading={isSubmitting}
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col lg:flex-row lg:max-w-xs">
            <Form.Field>
              <InputField
                behavior="default"
                error={validateFieldError(touched.teamName, errors.teamName)}
                label="Nombre de mi equipo"
                maxLength={30}
                name="teamName"
                placeholder="Ingresar"
                value={values.teamName ?? ''}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Form.Field>
          </div>
          <p className="text-sm font-bold text-left">Mi camiseta y escudo</p>
          <div className="flex gap-x-10">
            <div className="flex items-end">
              <figure className="lg:max-w-[135px] max-w-24 w-full">
                <TshirtTeam
                  withShield
                  shield={dataShieldTshirt?.shield}
                  tshirt={{
                    param5:
                      dataShieldTshirt?.tshirt?.param5 ??
                      defaultTshirtTeam.param5,
                    param1:
                      dataShieldTshirt?.tshirt?.param1 ??
                      defaultTshirtTeam.param1,
                    param2:
                      dataShieldTshirt?.tshirt?.param2 ??
                      defaultTshirtTeam.param2,
                    param3:
                      dataShieldTshirt?.tshirt?.param3 ??
                      defaultTshirtTeam.param3,
                    param4:
                      dataShieldTshirt?.tshirt?.param4 ??
                      defaultTshirtTeam.param4,
                  }}
                />
              </figure>
              <PencilButton
                onClick={() => navigate(ROUTES.editAccount.tshirtPanel)}
              />
            </div>
            <div className="flex items-end">
              <figure>
                <ShieldTeam
                  backgroundColor={`#${
                    dataShieldTshirt?.shield.escudoparam4 ??
                    defaultShieldTeam.escudoparam4
                  }`}
                  className="!w-[102px] h-[115px] lg:!w-[140px] lg:h-[158px]"
                  id={
                    dataShieldTshirt?.shield.escudoparam1 ??
                    defaultShieldTeam.escudoparam1
                  }
                  pattern={
                    dataShieldTshirt?.shield.escudoparam2 ??
                    defaultShieldTeam.escudoparam2
                  }
                  patternColor={`#${
                    dataShieldTshirt?.shield.escudoparam3 ??
                    defaultShieldTeam.escudoparam3
                  }`}
                  textContent={
                    dataShieldTshirt?.shield.escudoparam5 ??
                    defaultShieldTeam.escudoparam5
                  }
                />
              </figure>
              <PencilButton
                onClick={() => navigate(ROUTES.editAccount.shieldPanel)}
              />
            </div>
          </div>
          <div className="flex flex-col gap-y-4  lg:flex-row lg:gap-8 lg:gap-y-0">
            {isMobile ? (
              <div className="fixed bottom-0 left-0 right-0 bg-white p-4 z-50">
                <Button
                  className="w-full py-2 px-4"
                  disabled={!dirty || isSubmitting}
                  type="submit"
                  variant={'primary'}
                >
                  Guardar cambios
                </Button>
              </div>
            ) : (
              <Button
                className="mb-4 max-w-full lg:mt-12"
                disabled={!isFormDirty || isSubmitting}
                type="submit"
                variant={'primary'}
              >
                Guardar cambios
              </Button>
            )}
          </div>
        </Form.Form>
      </div>
    </>
  );
};
